import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { fetchIbamaCase } from '@/services/ibama';

const CNPJ_DIGITS = 14;

/**
 * One auto in the debt ledger: its issuance record, its walk through the
 * instances, its collection state and the ledger cases like it.
 *
 * Every `Answer` here is record-scoped: a refusal means THIS case lacks the
 * substrate (no master row, no comparable group), never that IBAMA cannot
 * support the analysis. The walk carries money on the issuance stage only:
 * the ledger dates the instances and never records what either did to the
 * fine, and its Valor Pago is shown as published, never read as a payment.
 */
export function IbamaCaseDetailPage() {
  const { key = '' } = useParams();
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'case', key],
    queryFn: () => fetchIbamaCase(key),
    enabled: key.length > 0,
    // A 404 is an answer (no such auto and UF in the ledger), not a transient failure.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 });
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-6">
        <ErrorMessage
          message={notFound ? t('ibama:case.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const c = data.case;
  const money = (value: number | null, inBrl: boolean, unit: string | null) =>
    value === null
      ? '—'
      : inBrl
        ? currency.format(value)
        : `${number.format(value)} ${unit ?? ''}`.trim();

  const facts: [string, string][] = [
    [t('ibama:case.facts.autoRaw'), c.notice_number_raw ?? c.notice_number],
    [t('ibama:case.facts.uf'), c.uf],
    [t('ibama:case.facts.municipality'), c.municipality ?? '—'],
    [t('ibama:case.facts.process'), c.process_number ?? '—'],
    [t('ibama:case.facts.nature'), c.nature ?? '—'],
    [t('ibama:case.facts.penaltyType'), c.penalty_type ?? '—'],
    [t('ibama:case.facts.noticeDate'), c.notice_date ?? '—'],
    [t('ibama:case.facts.currency'), c.currency ?? '—'],
    [t('ibama:case.facts.ledgerRows'), number.format(data.record.ledger_row_count)],
    [t('ibama:case.facts.reportUpdated'), data.record.report_updated_at?.slice(0, 10) ?? '—'],
  ];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/ibama/cases" className="text-primary underline-offset-2 hover:underline">
            {t('ibama:case.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold tabular-nums text-foreground">
          {t('ibama:case.title', { auto: c.notice_number, uf: c.uf })}
        </h1>
        <p className="text-sm text-muted-foreground" data-subject={c.entity_type ?? ''}>
          {c.entity_type === 'company' && c.entity_cnpj && c.entity_cnpj.length === CNPJ_DIGITS ? (
            <Link
              to={`/ibama/e/${c.entity_cnpj}`}
              className="text-primary underline-offset-2 hover:underline"
            >
              {c.entity_name ?? c.entity_cnpj}
            </Link>
          ) : c.entity_type === 'person' ? (
            <span className="italic">{t('ibama:case.subjectPerson')}</span>
          ) : (
            <span className="italic">{t('ibama:case.subjectUnknown')}</span>
          )}
        </p>
      </header>

      <ExplainerCard i18nKey="ibama:case.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-facts>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:case.facts.title')}
        </h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {facts.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3 border-b py-1">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="text-right text-foreground">{value}</dd>
            </div>
          ))}
        </dl>
        {c.legal_classification ? (
          <p className="mt-2 text-xs text-muted-foreground" data-classification>
            {t('ibama:case.facts.classification')}: {c.legal_classification}
          </p>
        ) : null}
      </section>

      <section className="rounded-lg border bg-card p-4" data-walk>
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('ibama:case.walk.title')}</h2>
        <Answer answer={data.walk} scope="record" help={t('ibama:case.walk.help')}>
          {(walk) => (
            <div className="space-y-3">
              <ol className="grid gap-3 sm:grid-cols-4">
                {walk.stages.map((stage) => (
                  <li
                    key={stage.stage}
                    className="rounded-md border bg-muted/30 p-3"
                    data-stage={stage.stage}
                  >
                    <div className="text-xs text-muted-foreground">
                      {t(`ibama:case.walk.stages.${stage.stage}`)}
                    </div>
                    <div className="mt-1 text-base font-semibold tabular-nums text-foreground">
                      {stage.stage === 'ISSUED'
                        ? money(stage.amount, walk.in_brl, walk.currency)
                        : t('ibama:case.walk.noAmount')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stage.stage_date ?? t('ibama:case.walk.undated')}
                    </div>
                    {stage.label ? (
                      <div className="mt-1 text-xs text-foreground">{stage.label}</div>
                    ) : null}
                  </li>
                ))}
              </ol>
              <p className="text-xs text-muted-foreground" data-published-paid>
                {walk.published_paid_amount === null
                  ? t('ibama:case.walk.noPublishedPaid')
                  : t('ibama:case.walk.publishedPaid', {
                      amount: money(walk.published_paid_amount, walk.in_brl, walk.currency),
                    })}
              </p>
              {/* Two independent facts, not an either/or: a case can have no
                  instance date at all AND carry an undated board outcome
                  code, and the backend emits both notes when it does. */}
              {!walk.first_decided && !walk.board_dated ? (
                <p className="text-xs text-muted-foreground">{t('ibama:case.walk.neitherDated')}</p>
              ) : null}
              {walk.board_outcome_recorded && !walk.board_dated ? (
                <p className="text-xs text-muted-foreground" data-outcome-undated>
                  {t('ibama:case.walk.outcomeUndated')}
                </p>
              ) : null}
              {!walk.in_brl && walk.currency ? (
                <p className="text-xs text-muted-foreground" data-currency-note>
                  {t('ibama:case.walk.preReal', { currency: walk.currency })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ibama:case.guide.walk" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-issuance>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:case.issuance.title')}
        </h2>
        <Answer answer={data.issuance} scope="record" help={t('ibama:case.issuance.help')}>
          {(m) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {(
                  [
                    ['issuedAt', m.issued_at?.slice(0, 10) ?? '—'],
                    ['acknowledgedAt', m.acknowledged_at?.slice(0, 10) ?? '—'],
                    ['factDate', m.fact_date ?? '—'],
                    ['autoType', m.auto_type ?? '—'],
                    ['fineType', m.fine_type ?? '—'],
                    ['infractionType', m.infraction_type ?? '—'],
                    ['infraction', m.infraction_description ?? '—'],
                    ['gravity', m.gravity ?? m.gravity_level ?? '—'],
                    ['areaHa', m.area_ha === null ? '—' : number.format(m.area_ha)],
                    ['biomes', m.biomes ?? '—'],
                    ['conservationUnit', m.conservation_unit ?? '—'],
                    ['actionType', m.action_type ?? '—'],
                    ['operation', m.operation_name ?? '—'],
                    ['controlUnit', m.control_unit ?? '—'],
                    ['registryOrigin', m.registry_origin ?? '—'],
                    ['deliveryForm', m.delivery_form ?? '—'],
                    [
                      'cancelled',
                      m.cancelled === null
                        ? '—'
                        : m.cancelled
                          ? t('ibama:case.issuance.yes')
                          : t('ibama:case.issuance.no'),
                    ],
                    ['formStatus', m.form_status ?? '—'],
                    ['seizures', number.format(m.seizure_terms)],
                    ['suspensions', number.format(m.suspension_terms)],
                  ] as const
                ).map(([field, value]) => (
                  <div
                    key={field}
                    className="flex justify-between gap-3 border-b py-1"
                    data-field={field}
                  >
                    <dt className="text-muted-foreground">
                      {t(`ibama:case.issuance.fields.${field}`)}
                    </dt>
                    <dd className="text-right text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
              {m.conduct_description ? (
                <p className="text-xs text-muted-foreground" data-conduct>
                  {m.conduct_description}
                </p>
              ) : null}
              {m.provisions.length ? (
                <ul className="flex flex-wrap gap-2 text-xs" data-provisions>
                  {m.provisions.map((provision) => (
                    <li key={provision} className="rounded border px-2 py-1 text-muted-foreground">
                      {provision}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('ibama:case.issuance.noProvisions')}
                </p>
              )}
              {m.key_row_count > 1 ? (
                <p className="text-xs text-muted-foreground">
                  {t('ibama:case.issuance.reused', { n: number.format(m.key_row_count) })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-collection>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:case.collection.title')}
        </h2>
        <Answer answer={data.collection} scope="record" help={t('ibama:case.collection.help')}>
          {(k) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {(
                  [
                    ['creditStatus', k.credit_status ?? '—'],
                    [
                      'outcome',
                      k.collection_outcome
                        ? t(`ibama:overview.outcomes.${k.collection_outcome}`, {
                            defaultValue: k.collection_outcome,
                          })
                        : '—',
                    ],
                    ['statusDate', k.status_date ?? '—'],
                    ['publishedPaid', money(k.published_paid_amount, c.in_brl, c.currency)],
                    ['instalmentRows', number.format(k.instalment_rows)],
                    [
                      'instalmentPlan',
                      k.instalment_plan_size === null ? '—' : number.format(k.instalment_plan_size),
                    ],
                  ] as const
                ).map(([field, value]) => (
                  <div
                    key={field}
                    className="flex justify-between gap-3 border-b py-1"
                    data-field={field}
                  >
                    <dt className="text-muted-foreground">
                      {t(`ibama:case.collection.fields.${field}`)}
                    </dt>
                    <dd className="text-right text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
              {k.events.length > 1 ? (
                <div className="overflow-x-auto rounded-md border" data-events>
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 text-left text-muted-foreground">
                      <tr>
                        <th className="px-2 py-1 font-medium">
                          {t('ibama:case.collection.events.status')}
                        </th>
                        <th className="px-2 py-1 font-medium">
                          {t('ibama:case.collection.events.statusDate')}
                        </th>
                        <th className="px-2 py-1 font-medium">
                          {t('ibama:case.collection.events.boardOutcome')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium">
                          {t('ibama:case.collection.events.publishedPaid')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {k.events.map((event, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-2 py-1 text-foreground">
                            {event.credit_status ?? '—'}
                          </td>
                          <td className="px-2 py-1 tabular-nums text-muted-foreground">
                            {event.status_date ?? '—'}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">
                            {event.board_outcome ?? '—'}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                            {money(event.published_paid_amount, c.in_brl, c.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-comparables>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:case.comparables.title')}
        </h2>
        <Answer answer={data.comparables} scope="record" help={t('ibama:case.comparables.help')}>
          {(g) => (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {t('ibama:case.comparables.basis', {
                  nature: g.nature,
                  classification: g.legal_classification ?? '—',
                  year: g.notice_year,
                })}
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(
                  [
                    ['members', number.format(g.member_count)],
                    ['companies', number.format(g.company_count)],
                    ['persons', number.format(g.person_count)],
                    [
                      'medianFaceValue',
                      g.amount_median === null ? '—' : currency.format(g.amount_median),
                    ],
                  ] as const
                ).map(([field, value]) => (
                  <div key={field} className="rounded-md border bg-muted/30 p-2" data-group={field}>
                    <div className="text-xs text-muted-foreground">
                      {t(`ibama:case.comparables.${field}`)}
                    </div>
                    <div className="font-semibold tabular-nums text-foreground">{value}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('ibama:case.comparables.settledShare', {
                  share: percent.format(g.settled_share),
                })}
              </p>
              <ul className="flex flex-wrap gap-2 text-xs">
                {Object.entries(g.outcome_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([outcome, count]) => (
                    <li key={outcome} className="rounded border px-2 py-1 text-muted-foreground">
                      {t(`ibama:overview.outcomes.${outcome}`, { defaultValue: outcome })}:{' '}
                      <span className="tabular-nums text-foreground">{number.format(count)}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ibama:case.guide.comparables" />
        </div>
      </section>
    </div>
  );
}
